import React, { type ForwardedRef, type HTMLAttributes, type JSX } from 'react';
import styled, { css } from 'styled-components';
import { Picture } from '@/app/components/hotel-detail/ImageWithFallback';
import { useImagePreload } from '@/app/components/hotel-detail/hooks/useImagePreload';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';

export type ImageSlideProps = {
  url: string;
  sources?: { srcSet: string; media?: string }[];
  loading?: 'eager' | 'lazy';
  overlay?: JSX.Element | null;
  containerRef?: ForwardedRef<HTMLDivElement>;
} & HTMLAttributes<HTMLDivElement>;

const ImageSlideOverlay = styled.div`
  > * {
    position: absolute;
  }
`;

/**
 * Single Slide of the gallery. Handles scroll snapping etc.
 */
function ImageSlide(props: ImageSlideProps) {
  const { url, sources, loading = 'lazy', overlay, containerRef, ...rest } = props;

  return (
    <div
      {...rest}
      ref={containerRef}
      css={css`
        flex: 0 0 calc(100vw - 32px);
        scroll-snap-align: center;
        scroll-snap-stop: always;
        position: relative;

        @media ${customCss.breakpoints.xs} {
          flex: 0 0 calc(100vw - 36px);
        }

        @media ${customCss.breakpoints.sm} {
          -webkit-transform: translate3d(0, 0, 0);
        }

        @media ${customCss.breakpoints.lg} {
          cursor: pointer;
        }
      `}
    >
      <Picture
        alt=""
        url={url}
        sources={sources}
        loading={loading}
        pictureCss={css`
          display: block;
          width: 100%;
          height: 100%;
        `}
        css={css`
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 1s;
          transform: scale3d(1, 1, 1);
          cursor: pointer;
          @media ${customCss.breakpoints.lg} {
            border-top-left-radius: 16px;
            border-bottom-left-radius: 16px;
          }
        `}
      />
      {overlay && <ImageSlideOverlay>{overlay}</ImageSlideOverlay>}
    </div>
  );
}

type ReallyLazyImageSlideProps = {
  currentIndex: number;
  numberOfImages: number;
  index: number;
  preload: number;
} & ImageSlideProps &
  HTMLAttributes<HTMLDivElement>;

/**
 * Same as `ImageSlide`, but loads the image only if should preload.
 *
 * It preloads the image based in the current index visible in the slider gallery and
 * the index of the image. Preload number in `useImagePreload` hook, specifies how
 * many images to the right of the image visible should be preloaded.
 * Thinking that navigation using left arrow is less usual, only is preloaded one image
 * to the left of the current image.
 *
 * Using css snap scroll points and adding a new preloaded image to the slider container
 * can cause scroll positions variations, and user can see jumps from one image to another.
 * To avoid this it loads an empty placeholder image to fill the spaces of images not preloaded yet.
 *
 * Note: current chrome would not need that as we use `loading="lazy"` anyhow.
 * All other browsers do need it thats why it is called **Really**Lazy 😅
 *
 */
function ReallyLazyImageSlide(props: ReallyLazyImageSlideProps) {
  const { currentIndex, index, url, sources, numberOfImages, preload, ...rest } = props;
  const [shouldRender] = useImagePreload({
    currentIndex,
    index,
    numberOfImages,
    preload,
  });
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;

  return shouldRender ? (
    <ImageSlide url={url} sources={sources} loading="eager" {...rest} />
  ) : (
    <ImageSlide url={`${imageBasePath}empty_placeholder.svg`} loading="eager" {...rest} />
  );
}

export { ImageSlide, ReallyLazyImageSlide };
